import {
  FlashOrderStatusCode,
  flashOrderStatusMessages,
  getConvertAskPrice,
  GetConvertAskPriceParams,
  getConvertFeeRate,
  postFlashOrder,
  PostFlashOrderParams,
} from 'api/v1/convert'
import { getSlot, SlotType } from 'api/v1/slot'
import Loading from 'components/Loading'
import toastify from 'components/Toastify'
import { SUB_ACCOUNT_ID } from 'config'
import { CONVERT_STATUS_FAIL } from 'constants/convert'
import { BigNumber } from 'ethers'
import { memo, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useAccountInfo, useCurrencyBalance } from 'store/account/hooks'
import { updateCurrentConvertInfo } from 'store/convert/actions'
import {
  useConvertBaseToken,
  useConvertQuoteToken,
  useCurrentConvertInfo,
} from 'store/convert/hooks'
import { useLinkSigner } from 'store/link/hooks'
import { bn, parseEtherToWei, w2e } from 'utils/number'
import { signOrder } from 'utils/signer/transactions/order'
import { sdk } from 'utils/signer/utils'
import { ButtonGroups, Tip, RefreshTip } from './style'
import { updateModal } from '../../store/app/actions/modalActions'
import i18n from '../../i18n'
import { ORDER_FAIL_REASONS } from '../../constants/trade'
import { useSetAtom } from 'jotai'
import { ConvertResult } from './successTip'
import Decimal from 'decimal.js'
import { ReactComponent as SvgTip } from './assets/tip.svg'

const MAX_REFRESH_NUM = 5

enum OrderStatus {
  pending = 'pending',
  success = 'success',
  fail = 'fail',
}

export const ExchangeBtn = memo(() => {
  const dispatch = useDispatch()
  const signer = useLinkSigner()
  const { accountId, layer2Id } = useAccountInfo()
  const { token: baseSymbol, amount: baseAmount } = useConvertBaseToken()
  const { token: quoteSymbol, amount: quoteAmount } = useConvertQuoteToken()
  const {
    baseAmount: currentBaseAmount,
    expireTime,
    price: convertCurrentPrice,
    feeRate,
    baseRange,
    quoteRange,
  } = useCurrentConvertInfo()

  const setConvertResult = useSetAtom(ConvertResult)
  const [btnTip, setBtnTip] = useState<string>('')
  const [countdownCount, setCountdownCount] = useState(expireTime || 5)
  const [refreshNum, setRefreshNum] = useState<number>(0)

  const [isDisable, setIsDisable] = useState(true)
  const [askPrice, setAskPrice] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false)
  const [isShowRefresh, setIsShowRefresh] = useState<boolean>(false)
  const [isPlacing, setIsPlacing] = useState<boolean>(false)
  const [isEnough, setIsEnough] = useState<boolean>(true)
  const [pending, setPending] = useState<boolean>(false)

  const [orderStatus, setOrderStatus] = useState<OrderStatus>(
    OrderStatus.pending,
  )
  const countdownTimer = useRef<any>()
  const baseSymbolL2Balance =
    useCurrencyBalance(baseSymbol?.name!)?.available ?? '0'

  useEffect(() => {
    if (isRefreshing || isShowRefresh || isPlacing || !askPrice) {
      clearInterval(countdownTimer.current)
    } else {
      countdownTimer.current = setInterval(() => {
        const nextCountdownCount = countdownCount - 1
        if (nextCountdownCount < 0) {
          clearInterval(countdownTimer.current)
        } else {
          setCountdownCount(countdownCount - 1)
        }
      }, 1000)
    }
    return () => {
      clearInterval(countdownTimer.current)
    }
  }, [askPrice, isRefreshing, isShowRefresh, isPlacing, countdownCount])

  useEffect(() => {
    isRefreshing && getEnquiryData()
  }, [isRefreshing])

  useEffect(() => {
    isPlacing && handleConvert()
  }, [isPlacing])

  useEffect(() => {
    if (countdownCount === 0) {
      if (refreshNum < MAX_REFRESH_NUM) {
        setIsRefreshing(true)
      } else {
        setIsShowRefresh(true)
      }
    }
  }, [countdownCount, refreshNum])

  useEffect(() => {
    if (!baseSymbol?.id || !quoteSymbol?.id) {
      setBtnTip(`Please select a token`)
      setIsShowRefresh(false)
      setIsDisable(true)
    } else if (!baseAmount && !quoteAmount) {
      setBtnTip(`Enter an amount`)
      setIsDisable(true)
      setIsShowRefresh(false)
    } else if (
      baseAmount &&
      parseEtherToWei(baseAmount).gt(baseSymbolL2Balance)
    ) {
      setBtnTip(`no enough ${baseSymbol?.name}`)
      setIsShowRefresh(false)
      setIsDisable(true)
    } else {
      setBtnTip(``)
      setIsDisable(false)
      setIsShowRefresh(false)
    }

    if (baseAmount && baseRange) {
      const { minSize: baseMinSize, maxSize: baseMaxSize } = baseRange
      if (
        parseEtherToWei(baseAmount).gt(parseEtherToWei(baseMaxSize)) ||
        parseEtherToWei(baseAmount).lt(parseEtherToWei(baseMinSize))
      ) {
        setIsDisable(true)
        setBtnTip(`Enter a legal quantity`)
      }
    }
    if (quoteAmount && quoteRange) {
      const { minSize: baseMinSize, maxSize: baseMaxSize } = quoteRange
      if (
        parseEtherToWei(quoteAmount).gt(parseEtherToWei(baseMaxSize)) ||
        parseEtherToWei(quoteAmount).lt(parseEtherToWei(baseMinSize))
      ) {
        setIsDisable(true)
        setBtnTip(`Enter a legal quantity`)
      }
    }
  }, [baseSymbol, quoteSymbol, baseAmount, quoteAmount, baseRange, quoteRange])

  useEffect(() => {
    setAskPrice(false)
    setRefreshNum(0)
  }, [baseSymbol, quoteSymbol, baseAmount, quoteAmount])

  const getEnquiryData = async () => {
    try {
      const getConvertAskPriceParam: GetConvertAskPriceParams = {
        fromCurrency: baseSymbol?.name!,
        toCurrency: quoteSymbol?.name!,
      }
      if (baseAmount) {
        getConvertAskPriceParam.fromAmount = baseAmount
      }
      if (quoteAmount) {
        getConvertAskPriceParam.toAmount = quoteAmount
      }

      const { price, expireTime, priceFrom, priceTo } =
        await getConvertAskPrice(getConvertAskPriceParam)

      dispatch(
        updateCurrentConvertInfo({
          expireTime,
          price: price + '',
          priceFrom,
          priceTo,
        }),
      )
      setCountdownCount(expireTime)
      setIsRefreshing(false)
      setAskPrice(true)
      setPending(false)
      setRefreshNum(refreshNum + 1)
    } catch (e) {
      console.error(e)
      toastify.error(e.message)
      setIsRefreshing(false)
      setAskPrice(false)
      setPending(false)
    }
  }

  const onRefresh = () => {
    setRefreshNum(0)
    setIsRefreshing(true)
    setIsPlacing(false)
    setIsShowRefresh(false)
  }

  const handleConvert = async () => {
    if (!isPlacing || !convertCurrentPrice || !isEnough) return
    setIsPlacing(true)
    setOrderStatus(OrderStatus.pending)
    try {
      const { data: orderSlot } = await getSlot(SlotType.Spot)

      const p: BigNumber = parseEtherToWei(convertCurrentPrice)
      const q: BigNumber = bn(
        sdk.closestPackableTransactionAmount(
          parseEtherToWei(currentBaseAmount as string).toString(),
        ),
      )
      const signedData = await signOrder(signer!, {
        accountId: layer2Id!,
        subAccountId: SUB_ACCOUNT_ID,
        slotId: orderSlot?.slotId,
        baseTokenId: baseSymbol?.l2CurrencyId!,
        quoteTokenId: quoteSymbol?.l2CurrencyId!,
        price: p.toString(),
        amount: q.toString(),
        isSell: true,
        feeRates: [0, feeRate! * 10000],
        nonce: orderSlot.nonce,
      })

      let orderPayload: PostFlashOrderParams = {
        fromCurrency: baseSymbol?.name!,
        toCurrency: quoteSymbol?.name!,
        direction: 'SHORT',
        price: convertCurrentPrice,
        quantity: w2e(q)!,
        slotId: orderSlot?.slotId,
        nonce: orderSlot.nonce,
        signature: signedData.tx.signature.signature,
      }

      const result = await postFlashOrder(accountId!, orderPayload)

      const { status } = result
      if (CONVERT_STATUS_FAIL.includes(status)) {
        const message = flashOrderStatusMessages[status as FlashOrderStatusCode]
        toastify.success(message)
      } else {
        toastify.success(i18n.t('order-submitted'))
      }

      setConvertResult({
        show: true,
        value: Decimal.mul(
          Decimal.mul((result as any).data.data.price, w2e(q)!),
          Decimal.sub(1, feeRate!),
        ).toFixed(),
        symbol: quoteSymbol?.name!,
      })
      setOrderStatus(OrderStatus.success)
      dispatch(
        updateModal({
          modal: 'convert',
          open: false,
        }),
      )
    } catch (e) {
      if (e?.message) {
        toastify.error(
          ORDER_FAIL_REASONS[e.message as keyof typeof ORDER_FAIL_REASONS] ??
            e?.message,
        )
      }
      console.error(e)
      setOrderStatus(OrderStatus.fail)
    }
    setIsPlacing(false)
    setIsShowRefresh(true)
  }

  useEffect(() => {
    if (pending) {
      getConvertFeeRate(baseSymbol?.name!, quoteSymbol?.name!).then((res) => {
        dispatch(
          updateCurrentConvertInfo({
            feeRate: res.feeRate,
          }),
        )
        setRefreshNum(0)
        setIsRefreshing(true)
      })
    }
  }, [pending])

  useEffect(() => {
    if (
      !currentBaseAmount ||
      orderStatus !== OrderStatus.pending ||
      isPlacing ||
      !baseSymbol
    )
      return

    if (parseEtherToWei(currentBaseAmount).gt(baseSymbolL2Balance)) {
      setIsEnough(false)
    } else {
      setIsEnough(true)
    }
  }, [currentBaseAmount, baseSymbolL2Balance, orderStatus, isPlacing])

  const renderBtn = () => {
    if (!askPrice) {
      if (isDisable) {
        return <div>{btnTip}</div>
      } else {
        return (
          <div
            onClick={() => {
              if (isDisable || pending) return
              setRefreshNum(0)
              setIsShowRefresh(false)
              setPending(true)
            }}
          >
            {pending ? <Loading /> : null} <span>Preview Conversion</span>
          </div>
        )
      }
    } else if (isPlacing) {
      return (
        <div>
          <Loading /> <span>Placing</span>
        </div>
      )
    } else if (isRefreshing) {
      return (
        <div>
          <Loading />
          <span> Refreshing</span>
        </div>
      )
    } else if (isShowRefresh) {
      return <div onClick={onRefresh}>Refresh Quote</div>
    } else {
      return (
        <div onClick={() => setIsPlacing(true)}>
          Convert ({countdownCount}s)
        </div>
      )
    }
  }

  const renderTip = () => {
    if (!isEnough) {
      return (
        <Tip>
          Your account has insufficient balance. Please fund your account.
        </Tip>
      )
    } else if (isShowRefresh) {
      return (
        <RefreshTip>
          <SvgTip />
          Quote expired. Please request a new quote.
        </RefreshTip>
      )
    }
    return <></>
  }

  return (
    <>
      {renderTip()}
      {/* {!isEnough && (
        <Tip>
          Your account has insufficient balance. Please fund your account.
        </Tip>
      )}
      {isShowRefresh && (
        <RefreshTip>
          <SvgTip />
          Quote expired. Please request a new quote.
        </RefreshTip>
      )} */}

      <ButtonGroups
        className={
          pending || isDisable || isRefreshing || !isEnough || isPlacing
            ? 'disable'
            : 'active'
        }
      >
        {renderBtn()}
      </ButtonGroups>
    </>
  )
})
